"use client";

import { createContext } from "react";

import { analyticsEvent } from "@up/analytics";
import { useCart } from "@up/checkout";
import {
  DEADLINE_PROPERTY_MAP,
  ProfileType,
  applicationIsApproved,
  logError,
  useOwnerProfile,
  useProfile,
  useTheme,
} from "@up/data";

import { isCartOpen } from "./is-cart-open-store";

import type { TDeadlineKey, TInventoryItem } from "@up/data";
import type { LinkProps } from "next/link";
import type { ReactNode } from "react";

export type CohortCartDataType = {
  cohort: TInventoryItem;
  deadline?: string;
  deadlineKey?: TDeadlineKey;
  isAddingItem: boolean;
  isCoursePage: boolean;
  isFull: boolean;
  showAddToCart: boolean;
  url: LinkProps["href"];
  addToCart: () => Promise<void>;
};

export const CohortCartDataContext = createContext<CohortCartDataType>({
  cohort: {} as TInventoryItem,
  isAddingItem: false,
  isCoursePage: false,
  isFull: false,
  showAddToCart: false,
  url: "#",
  addToCart: async () => {
    /* empty */
  },
});

export const CohortCardData = function ({
  children,
  cohort,
  deadlineKey = "application",
  isCoursePage = false,
  url,
}: {
  children: ReactNode;
  cohort: TInventoryItem;
  deadlineKey?: TDeadlineKey;
  isCoursePage?: boolean;
  url: LinkProps["href"];
}) {
  const theme = useTheme();
  const profile = useProfile();
  const ownerProfile = useOwnerProfile();
  const cart = useCart();

  const isFull = cohort.closed;

  const deadline =
    theme.directToCheckout && deadlineKey
      ? undefined
      : cohort[DEADLINE_PROPERTY_MAP[deadlineKey]];

  const showAddToCart = (() => {
    if (theme.directToCheckout) {
      return true;
    }

    if (profile?.data?.type === ProfileType.Guardian) {
      return true;
    }

    if (
      profile?.data?.type === ProfileType.Student &&
      applicationIsApproved(profile.contact?.application?.status)
    ) {
      return true;
    }

    return false;
  })();

  const CAN_ADD_TO_CART =
    theme.directToCheckout ||
    applicationIsApproved(ownerProfile?.application?.status);

  const addToCart = async () => {
    isCartOpen.set(true);

    if (CAN_ADD_TO_CART) {
      analyticsEvent({
        event: "addToCart",
        location: "cohort-slice",
      });
      try {
        await cart.addItem(cohort._id);
      } catch (err) {
        logError({
          action: "COHORT_CARD_ADD_TO_CART",
          error: err,
          context: { cohortId: cohort._id },
        });
      }
    }
  };

  return (
    <CohortCartDataContext.Provider
      value={{
        cohort,
        deadlineKey,
        isAddingItem: cart.isAddingItem,
        isCoursePage,
        isFull,
        deadline,
        showAddToCart,
        url,
        addToCart,
      }}
    >
      {children}
    </CohortCartDataContext.Provider>
  );
};
